<script>
import _ from 'lodash'
import Request from '@src/mixins/request'
import reservationItems from '@src/data/reservation-items.json'
import { authComputed } from '@state/helpers'

export default {
  mixins: [Request],

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      color: 'gold',
      viewCC: false,
      token: '',
      ccNumber: '',
      reservationItems,
    }
  },

  computed: {
    ...authComputed,
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    cardNumber: {
      get() {
        const cardNumber = this.item.payment ? this.item.payment.cardNumber : ''
        return this.ccNumber || cardNumber
      },
      set(val) {
        this.ccNumber = val
      },
    },
  },

  watch: {
    dialog(val) {
      if (!val) {
        this.token = ''
        this.ccNumber = ''
        this.viewCC = false
      }
    },
  },

  methods: {
    getValue(key) {
      return _.get(this.item, key)
    },
    async requestToken() {
      await this.fetchData({
        url: this.endpoint.reservation.requestToken,
        data: { reservationId: this.item.id },
      })
      this.$store.commit('app/SET_SNACKBAR', {
        text: 'The token was sent to your email',
      })
      this.viewCC = true
    },
    async getCardNumber() {
      // api request
      this.fetchData({
        url: this.endpoint.reservation.getCardNumber,
        data: { token: this.token },
      })
        .then(({ card }) => {
          this.ccNumber = card
        })
        .catch(() => {})
      // hide view link and text input
    },
    can(permission) {
      return this.havePermission(permission)
    },
  },
}
</script>

<template>
  <VDialog v-model="dialog" max-width="720px" persistent>
    <VCard>
      <VCardText>
        <VLayout row wrap>
          <!-- Reservation Items -->
          <VFlex
            v-for="(list, index) in reservationItems.details"
            :key="`${index}-${list.title}`"
            v-bind="{ [list.size]: '' }"
            class="mb-4"
            xs12
          >
            <div class="title mb-2" :class="`${color}--text`">
              {{ list.title }}
            </div>

            <template v-for="listItem in list.items">
              <VLayout
                v-if="listItem.key === 'payment.cardNumber'"
                :key="listItem.key"
                row
                wrap
                align-center
              >
                <VFlex xs4>
                  <VSubheader style="height: 25px;">{{
                    listItem.label
                  }}</VSubheader>
                </VFlex>

                <VFlex xs8>
                  <div class="mb-0">
                    <VContainer grid-list-xs justify-center pa-0>
                      <VLayout wrap>
                        <VFlex xs12 sm4 align-self-center>{{
                          cardNumber
                        }}</VFlex>
                        <template v-if="isAdmin || can('show-credit-card')">
                          <VFlex v-if="!ccNumber" xs12 sm8>
                            <template>
                              <VTextField
                                v-if="viewCC"
                                v-model="token"
                                label="Access token"
                                class="pt-0"
                                append-outer-icon="lock_open"
                                color="gold"
                                autofocus
                                @click:append-outer="getCardNumber"
                              />
                              <a v-else @click="requestToken">View</a>
                            </template>
                          </VFlex>
                        </template>
                      </VLayout>
                    </VContainer>
                  </div>
                </VFlex>
              </VLayout>

              <BaseDisplayText
                v-else
                :key="listItem.key"
                :label="listItem.label"
                :text="getValue(listItem.key)"
              />
            </template>
          </VFlex>
          <!-- End of Reservation Items -->

          <!-- Breakdown -->
          <VFlex class="mb-4" xs12>
            <div class="title mb-2" :class="`${color}--text`">
              Breakdown
            </div>

            <div class="px-3">
              <template v-for="(roomItem, index) in item.rooms">
                <VLayout :key="`room_${index}`" row>
                  <VFlex sm10>
                    <VLayout row>
                      <VFlex sm6>
                        <span class="d-block subheading">
                          {{ roomItem.name }}
                        </span>
                        <span class="d-block">
                          {{ roomItem.ratePlan }}
                        </span>
                        <span class="d-block caption">
                          {{ roomItem.cancellation }}
                        </span>
                      </VFlex>
                      <VFlex sm6>
                        <ul>
                          <li v-if="roomItem.inclusiveBreakfast">
                            Breakfast included
                          </li>
                          <li
                            v-for="(benefit, i) in roomItem.benefits"
                            :key="`benefit_${i}`"
                          >
                            {{ benefit }}
                          </li>
                        </ul>
                      </VFlex>
                    </VLayout>
                  </VFlex>

                  <VFlex sm2 class="text-xs-right">
                    <span>{{ roomItem.price }}</span>
                  </VFlex>
                </VLayout>

                <VDivider :key="`rmd_${index}`" class="my-2" />

                <template v-for="(eb, ebIndex) in roomItem.extraBeds">
                  <VLayout :key="`eb_${ebIndex}`" row>
                    <VFlex sm10>
                      <span class="d-block subheading">
                        {{ eb.name }}
                      </span>
                      <span class="d-block caption">
                        {{ eb.room }}
                      </span>
                    </VFlex>

                    <VFlex sm2 class="text-xs-right">
                      <span>{{ eb.price }}</span>
                    </VFlex>
                  </VLayout>

                  <VDivider :key="`ebd_${ebIndex}`" class="my-2" />
                </template>
              </template>
            </div>

            <div class="px-3">
              <template v-for="(extraItem, index) in item.extras">
                <VLayout :key="`extra_${index}`" row>
                  <VFlex sm10>
                    <span class="d-block subheading">
                      {{ extraItem.name }}
                    </span>
                    <span class="d-block caption">
                      {{ extraItem.dateTime }}
                    </span>
                  </VFlex>

                  <VFlex sm2 class="text-xs-right">
                    <span>{{ extraItem.price }}</span>
                  </VFlex>
                </VLayout>
                <VDivider :key="`exd_${index}`" class="my-2" />
              </template>
            </div>

            <div class="px-3">
              <VLayout
                v-for="summary in reservationItems.summary"
                :key="summary.key"
                row
              >
                <VFlex xs10 class="text-xs-right">
                  <span>{{ summary.label }}</span>
                </VFlex>
                <VFlex xs2 class="text-xs-right">
                  <span>{{ getValue(summary.key) }}</span>
                </VFlex>
              </VLayout>
            </div>
          </VFlex>
          <!-- End of Breakdown -->
        </VLayout>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn depressed dark :color="color" @click="dialog = false">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" module>
.summary-list {
  .v-list__tile {
    height: 30px;
  }
}
</style>
